
import React, { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import toast from "react-hot-toast";
import SkeletonList from "../common/SkeletonList";
import { convertAudioBlobToMp3 } from "../../utils/audioToMp3";

const getCookie = (name) => {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\/\+^])/g, "\\$1") + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const buildImageUrl = (path) => {
  if (!path) return "./chatscreen/profile.svg";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return `${process.env.REACT_APP_API_BASE_URL}${path}`;
  return path;
};

const buildMediaUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/")) return `${process.env.REACT_APP_API_BASE_URL}${path}`;
  return path;
};

const getSocketBaseUrl = () => {
  const apiBase = process.env.REACT_APP_API_BASE_URL || "";
  try {
    // If API base contains a path like /api, use only the origin for Socket.IO namespace.
    return new URL(apiBase).origin;
  } catch {
    // Fallback for relative URLs
    return apiBase;
  }
};

const getSocketConnectionConfig = () => {
  const socketBaseUrl = getSocketBaseUrl() || "https://phonebookplus.app";
  return {
    // Connect directly to the backend origin to avoid the broken localhost proxy path.
    url: `${socketBaseUrl}/admin-broadcast`,
    // Verified backend handshake endpoint.
    path: "/api/socket.io/",
  };
};

const isUserMessage = (message, activeUserId) => {
  const direction = String(message?.direction || "").toLowerCase();
  if (direction === "user_to_admin") return true;
  if (direction === "admin_to_user") return false;

  const senderName = String(message?.sender_name || message?.sender || "").trim().toLowerCase();
  if (senderName) {
    if (
      senderName.includes("admin")
      || senderName.includes("broadcast")
      || senderName.includes("support")
      || senderName.includes("phonebookplus")
    ) {
      return false;
    }
    return true;
  }

  const senderId = String(message?.sender_id || message?.from_user_id || "");
  if (senderId && activeUserId) return senderId === String(activeUserId);

  return false;
};

const unwrapSocketPayload = (payload) => {
  if (!payload || typeof payload !== "object") return {};
  if (payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return payload.data;
  }
  return payload;
};

const extractConversationUserId = (payload) => {
  const data = unwrapSocketPayload(payload);
  const direction = String(data?.direction || payload?.direction || "").toLowerCase();

  if (data?.user_id != null && data.user_id !== "") return String(data.user_id);
  if (payload?.user_id != null && payload.user_id !== "") return String(payload.user_id);

  if (direction === "user_to_admin") {
    const senderId = data?.sender_id || data?.from_user_id || payload?.sender_id;
    if (senderId != null && senderId !== "") return String(senderId);
  }

  if (direction === "admin_to_user") {
    const receiverId = data?.receiver_id || data?.to_user_id || data?.user_id;
    if (receiverId != null && receiverId !== "") return String(receiverId);
  }

  const senderId = data?.sender_id || data?.from_user_id || payload?.sender_id;
  const receiverId = data?.receiver_id || data?.to_user_id;
  if (senderId != null && senderId !== "") return String(senderId);
  if (receiverId != null && receiverId !== "") return String(receiverId);

  return "";
};

const getMessagePreviewText = (payload, fallbackMessage) => {
  const data = unwrapSocketPayload(payload);
  return (
    data?.message
    || data?.text
    || data?.last_message
    || payload?.message
    || payload?.last_message
    || fallbackMessage
    || ""
  );
};

const getMessageType = (message) => {
  const mime = String(message?.mime_type || message?.mime || message?.file_mime || "").toLowerCase();
  const fileUrl = message?.file || message?.file_url || message?.file_path || message?.url || "";
  const fileName = message?.filename || message?.file_name || message?.name || "";
  const explicitType = String(message?.type || "").toLowerCase();

  if (explicitType && explicitType !== "text") return explicitType;
  if (mime.startsWith("image")) return "image";
  if (mime.startsWith("audio")) return "audio";
  if (mime.startsWith("video")) return "video";
  if (fileUrl || fileName) return "document";
  return "text";
};

const getMessageTimestamp = (message) => {
  const original = message?.original || message || {};
  const raw = message?.time || original?.created_at || original?.timestamp || original?.time || "";
  const parsed = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

  function AudioMessage({ message, isUser, senderName }) {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const initialDuration = Number(message?.duration ?? message?.durationSeconds ?? message?.original?.duration ?? message?.original?.duration_seconds ?? 0);
    const [duration, setDuration] = useState(Number.isFinite(initialDuration) && initialDuration > 0 ? initialDuration : 0);
    const [isHovering, setIsHovering] = useState(false);

    const src = buildMediaUrl(message.fileUrl);
    const safeSenderName = typeof senderName === "string" ? senderName.trim() : "";

    const formatTime = (seconds) => {
      if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs.toString().padStart(2, "0")}`;
    };

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);

      // Prefer any duration already available on the audio element (e.g. local blob metadata)
      // fallback to the provided initial duration from the message payload.
      const audioDuration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null;
      const provided = Number.isFinite(initialDuration) && initialDuration > 0 ? initialDuration : 0;
      setDuration(audioDuration ?? provided);
    }, [src]);

    useEffect(() => {
      const audio = audioRef.current;
      if (!audio || !src) return;

      let _pollTimer = null;
      const syncDuration = () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
          return true;
        }
        return false;
      };

      const syncTime = () => {
        setCurrentTime(Number.isFinite(audio.currentTime) ? audio.currentTime : 0);
      };

      const handlePause = () => {
        setIsPlaying(false);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0);
      };

      const handleError = () => {
        setIsPlaying(false);
      };

      audio.addEventListener("loadedmetadata", syncDuration);
      audio.addEventListener("durationchange", syncDuration);
      audio.addEventListener("timeupdate", syncTime);
      audio.addEventListener("seeked", syncTime);
      audio.addEventListener("play", syncDuration);
      audio.addEventListener("pause", handlePause);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);

      // Ensure the audio element triggers a load for new sources (helps with object URLs)
      try {
        audio.load();
      } catch (err) {
        // ignore
      }

      // Poll a few times for duration if metadata isn't available immediately (helps with some browsers/CORS timing)
      const tryPoll = (attempts = 0) => {
        if (syncDuration()) return;
        if (attempts >= 8) return; // ~800ms total
        _pollTimer = setTimeout(() => tryPoll(attempts + 1), 100);
      };
      const fallback = setTimeout(() => tryPoll(0), 200);

      return () => {
        audio.removeEventListener("loadedmetadata", syncDuration);
        audio.removeEventListener("durationchange", syncDuration);
        audio.removeEventListener("timeupdate", syncTime);
        audio.removeEventListener("seeked", syncTime);
        audio.removeEventListener("play", syncDuration);
        audio.removeEventListener("pause", handlePause);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("error", handleError);
        clearTimeout(fallback);
        if (_pollTimer) clearTimeout(_pollTimer);
      };
    }, [src]);

    const handlePlayClick = async () => {
      const audio = audioRef.current;
      if (!audio || !src) return;

      if (!audio.paused) {
        audio.pause();
        return;
      }

      try {
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.then === "function") {
          await playPromise;
        }
      } catch (err) {
        setIsPlaying(false);
      }
    };

    const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0;

    return (
      <div className={`flex ${isUser ? "justify-start" : "justify-end"}`}>
        <div
          className={`w-full max-w-[220px] rounded-[24px] px-4 py-3 shadow-sm ${
            isUser ? "bg-white border border-gray-200" : "bg-[#FFF9FF] border border-[#E7D6FF]"
          }`}
        >
          {safeSenderName && (
            <p className={`mb-2 text-xs font-semibold ${isUser ? "text-gray-700" : "text-[#5D5FEF]"}`}>
              {safeSenderName}
            </p>
          )}

          <div className="mb-3 flex items-center gap-3">
            <button
              type="button"
              onClick={handlePlayClick}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              disabled={!src}
              className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full transition ${
                isUser ? "bg-[#F5F2FF] hover:bg-[#E9E1FF]" : "bg-[#5D5FEF] hover:bg-[#514BE8]"
              } ${!src ? "cursor-not-allowed opacity-50" : ""}`}
              aria-label={isPlaying ? "Pause audio" : "Play audio"}
            >
              <img
                src={
                  isPlaying
                    ? "../chatscreen/pause.svg"
                    : isHovering && !isUser
                      ? "../chatscreen/volume-white.svg"
                      : "../chatscreen/speaker.svg"
                }
                alt=""
                className="h-7 w-7"
                aria-hidden="true"
              />
            </button>

            <img
              src="../chatscreen/audio-wave.svg"
              alt=""
              aria-hidden="true"
              className="h-10 flex-1 select-none object-cover opacity-90"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className={`min-w-[38px] text-xs font-medium ${isUser ? "text-gray-500" : "text-[#5D5FEF]"}`}>
              {formatTime(currentTime)}
            </span>

            <div className="relative flex h-4 flex-1 items-center">
              <div className={`h-2 w-full rounded-full ${isUser ? "bg-gray-200" : "bg-[#E5DEFF]"}`} />
              <div
                className="absolute left-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-[#5D5FEF]"
                style={{ width: `${progress}%` }}
              />
              <input
                type="range"
                min="0"
                max={duration || 0}
                value={currentTime}
                step="0.01"
                onChange={(e) => {
                  const nextTime = Math.max(0, Math.min(duration || 0, parseFloat(e.target.value) || 0));
                  setCurrentTime(nextTime);
                  if (audioRef.current) {
                    audioRef.current.currentTime = nextTime;
                  }
                }}
                disabled={!src}
                className="absolute h-4 w-full cursor-pointer bg-transparent opacity-0"
                aria-label="Seek audio"
              />
            </div>

            <span className={`min-w-[38px] text-right text-xs font-medium ${isUser ? "text-gray-500" : "text-[#5D5FEF]"}`}>
              {formatTime(duration)}
            </span>
          </div>

          <p className={`mt-2 text-right text-[11px] ${isUser ? "text-gray-500" : "text-gray-600"}`}>
            {formatMessageTimeUtc(message.time)}
          </p>

          <audio
            ref={audioRef}
            src={src}
            preload="metadata"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => {
              setIsPlaying(false);
              setCurrentTime(Number.isFinite(audioRef.current?.duration) ? audioRef.current.duration : 0);
            }}
            className="hidden"
          />
        </div>
      </div>
    );
  }

const parseMessageDate = (rawTime) => {
  if (!rawTime) return null;
  const value = String(rawTime).trim();
  if (!value) return null;

  // Normalize higher precision fractional seconds for consistent Date parsing.
  const normalized = value.replace(/\.(\d{3})\d+([zZ]|[+-]\d{2}:\d{2})$/, ".$1$2");
  const parsed = new Date(normalized);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
};

const LOCAL_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

const formatMessageTimeUtc = (rawTime) => {
  const parsed = parseMessageDate(rawTime);
  if (!parsed) return "";
  return LOCAL_TIME_FORMATTER.format(parsed);
};

function AdminVoiceSendModal({ open, onClose, recipientName, onSend }) {
  const modalRef = useRef(null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const recordingStartedAtRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [chunks, setChunks] = useState([]);
  const [busy, setBusy] = useState(false);

  const formatTimer = (value) => {
    const normalized = Number.isFinite(value) ? value : 0;
    const totalSeconds = normalized > 0 ? Math.max(1, Math.ceil(normalized)) : 0;
    const mins = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
    const secs = (totalSeconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  const syncElapsedSeconds = () => {
    if (!recordingStartedAtRef.current) return 0;
    return (Date.now() - recordingStartedAtRef.current) / 1000;
  };

  const resetRecording = () => {
    setRecording(false);
    setElapsedSeconds(0);
    setChunks([]);
    recordingStartedAtRef.current = null;
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const stopRecording = () => {
    try {
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }
    } catch (error) {
      }
    stopStream();
  };

  useEffect(() => {
    if (!recording) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return;
    }

    timerRef.current = setInterval(() => {
      setElapsedSeconds(syncElapsedSeconds());
    }, 100);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [recording]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        try {
          recorderRef.current.stop();
        } catch (error) {
          }
      }
      stopStream();
    };
  }, []);

  useEffect(() => {
    if (!open) {
      setBusy(false);
      resetRecording();
    }
  }, [open]);

  if (!open) return null;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";

      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      setChunks([]);
      recordingStartedAtRef.current = Date.now();
      setElapsedSeconds(0);

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          setChunks((prev) => [...prev, event.data]);
        }
      };

      recorder.onstart = () => {
        recordingStartedAtRef.current = Date.now();
        setRecording(true);
      };
      recorder.onstop = () => {
        setRecording(false);
        setElapsedSeconds(syncElapsedSeconds());
      };

      recorder.start(500);
    } catch (error) {
      toast.error("Microphone access denied.");
    }
  };

  const toggleRecording = () => {
    if (recording) {
      stopRecording();
      return;
    }
    startRecording();
  };

  const handleClose = () => {
    stopRecording();
    resetRecording();
    onClose();
  };

  const handleDelete = () => {
    stopRecording();
    resetRecording();
    toast.dismiss();
    toast("Recording deleted successfully.");
    onClose();
  };

    const buildBlob = () => {
      if (!chunks.length) return null;
      return new Blob(chunks, { type: "audio/webm" });
    };

  const handleSend = async () => {
    if (busy) return;

    try {
      setBusy(true);

      if (recording && recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
        await new Promise((resolve) => setTimeout(resolve, 120));
      }

      const blob = buildBlob();
      if (!blob || blob.size === 0) {
        toast.error("No audio recorded. Please record something first.");
        setBusy(false);
        return;
      }

      const finalDurationSeconds = Math.max(elapsedSeconds, syncElapsedSeconds());

      await onSend?.({
        blob,
        filename: blob.type?.includes("webm") ? "admin-voice-message.webm" : "admin-voice-message.audio",
        mimeType: blob.type || "audio/webm",
        durationSeconds: finalDurationSeconds,
      });

      toast.success("Voice message sent!");
      onClose();
      resetRecording();
    } catch (error) {
      toast.error(error?.message || "Failed to send voice message");
    } finally {
      setBusy(false);
      stopStream();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm px-4">
      <div ref={modalRef} className="relative w-full max-w-sm rounded-[32px] bg-white px-8 pb-5 pt-7 shadow-2xl">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 transition hover:bg-gray-200"
          aria-label="Close"
        >
          <img src="../chatbox/close.svg" alt="close" className="h-4 w-4" />
        </button>
        <div className="mb-6 text-center">
          <p className="text-sm font-semibold text-[#6D5EF7]">Voice Message</p>
          <h3 className="mt-1 text-lg font-bold text-gray-900 truncate">
            {recipientName ? `Send to ${recipientName}` : "Select a user first"}
          </h3>
        </div>

        <div className="flex flex-col items-center gap-4">
          <button
            type="button"
            onClick={toggleRecording}
            className="rounded-full"
            aria-pressed={recording}
          >
            <img
              src={recording ? "../chatscreen/pause.svg" : "../chatscreen/main-mic.svg"}
              alt={recording ? "pause recording" : "start recording"}
              className={`h-28 w-28 ${recording ? "animate-pulse" : ""}`}
            />
          </button>

          <div className="text-2xl font-bold tracking-wider text-gray-900">{formatTimer(elapsedSeconds)}</div>

          <div className="flex items-center gap-4 pt-2">
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-xl bg-gray-100 p-3 transition hover:bg-gray-200"
              aria-label="Delete recording"
            >
              <img src="../chatscreen/trash.svg" alt="delete" className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={busy}
              className="rounded-xl bg-gray-100 p-3 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Send recording"
            >
              <img src="../chatscreen/send-arrow.svg" alt="send" className="h-8 w-8" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminChat() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 900 : false);
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'detail'
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [search, setSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);

  const token = getCookie("admin_token");
  const socketRef = useRef(null);
  const selectedUserRef = useRef(null);
  const messagesRef = useRef(null);
  const seenMessageKeysRef = useRef(new Set());

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.name} ${u.phone} ${u.lastMessage}`.toLowerCase().includes(q));
  }, [users, search]);

  const normalizeUsers = (payload = {}) => {
    // Handle multiple shapes:
    // 1) { data: [ { users: [ ... ] } ], status: '1' }
    // 2) { data: [ { ... } ] } (messages-style)
    // 3) { users: [ ... ] }
    // 4) [ { users: [...] } ]
    // 5) [ { user objects... } ]
    // 6) payload is already an array of user objects
    let source = [];

    if (payload == null) return [];
    if (Array.isArray(payload)) {
      // payload is an array; it may be [{ users: [...] }] or [{ user objects }]
      if (payload[0] && Array.isArray(payload[0].users)) source = payload[0].users;
      else source = payload;
    } else if (Array.isArray(payload.data)) {
      // payload.data is an array; check for users inside first element
      if (payload.data[0] && Array.isArray(payload.data[0].users)) source = payload.data[0].users;
      else source = payload.data;
    } else if (Array.isArray(payload.users)) {
      source = payload.users;
    } else if (Array.isArray(payload.data?.users)) {
      source = payload.data.users;
    }

    return (source || []).map((u) => ({
      id: u.user_id || u.id,
      name: u.name || u.full_name || `${u.first_name || ""} ${u.last_name || ""}`.trim() || "Unknown",
      phone: `${u.country_code || ""}${u.phone_number || u.phone || ""}`,
      lastMessage: u.last_message || u.message || "",
      lastMessageTime: u.last_message_time || u.created_at || "",
      avatar: buildImageUrl(u.profile_image_url || u.profile_image),
    }));
  };

  const normalizeMessages = (payload, activeUserId = selectedUserRef.current?.id) => {
    const source = Array.isArray(payload?.data?.messages)
      ? payload.data.messages
      : Array.isArray(payload?.messages)
        ? payload.messages
        : Array.isArray(payload?.data)
          ? payload.data[0]?.messages || []
          : Array.isArray(payload)
            ? payload
            : payload?.message_id || payload?.message || payload?.text || payload?.file || payload?.file_url || payload?.file_path || payload?.filename || payload?.file_name || payload?.url || payload?.type
              ? [payload]
              : [];

    return (source || [])
      .map((m) => ({
        id: m.message_id || m.id || `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        text: m.message || m.text || m.filename || m.file_name || m.name || "",
        time: m.created_at || m.time || "",
        isUser: isUserMessage(m, activeUserId),
        type: getMessageType(m),
        fileName: m.filename || m.file_name || m.name || "",
        fileUrl: m.file || m.file_url || m.file_path || m.url || "",
        mime: m.mime_type || m.mime || m.file_mime || "",
        duration: m.duration || m.duration_seconds || m.audio_duration || 0,
        senderName: m.sender_name || (String(m.direction || "").toLowerCase() === "user_to_admin" ? "User" : "Admin"),
        original: m,
      }))
      .sort((a, b) => getMessageTimestamp(a) - getMessageTimestamp(b));
  };

  const getMessageKey = (message) => {
    const original = message?.original || message || {};
    const parts = [
      message?.id || original?.message_id || original?.id || "",
      String(original?.sender_id || original?.from_user_id || original?.user_id || original?.sender_name || "").toLowerCase(),
      String(original?.receiver_id || original?.to_user_id || "").toLowerCase(),
      String(original?.direction || "").toLowerCase(),
      String(message?.type || original?.type || getMessageType(original)).toLowerCase(),
      String(message?.text || original?.message || original?.text || original?.filename || original?.file_name || original?.name || "").trim().toLowerCase(),
      String(message?.fileUrl || original?.file || original?.file_url || original?.file_path || original?.url || "").trim().toLowerCase(),
      String(message?.time || original?.created_at || original?.time || "").trim().toLowerCase(),
    ];
    return parts.join("|");
  };

  const getLivePayloadKey = (payload = {}) => {
    const original = payload?.data || payload || {};
    return [
      original?.message_id || original?.id || "",
      String(original?.sender_id || original?.from_user_id || original?.user_id || original?.sender_name || "").toLowerCase(),
      String(original?.receiver_id || original?.to_user_id || "").toLowerCase(),
      String(original?.direction || "").toLowerCase(),
      String(getMessageType(original)).toLowerCase(),
      String(original?.message || original?.text || original?.filename || original?.file_name || original?.name || "").trim().toLowerCase(),
      String(original?.file || original?.file_url || original?.file_path || original?.url || "").trim().toLowerCase(),
    ].join("|");
  };

  const getComparableContentKey = (message) => {
    const original = message?.original || message || {};
    return [
      String(message?.isUser ? "user" : "admin"),
      String(message?.type || getMessageType(original)).toLowerCase(),
      String(message?.text || original?.message || original?.text || original?.filename || original?.file_name || original?.name || "").trim().toLowerCase(),
      String(message?.fileUrl || original?.file || original?.file_url || original?.file_path || original?.url || "").trim().toLowerCase(),
      String(message?.fileName || original?.filename || original?.file_name || original?.name || "").trim().toLowerCase(),
    ].join("|");
  };

  const rememberMessages = (nextMessages) => {
    seenMessageKeysRef.current = new Set((nextMessages || []).map(getMessageKey));
  };

  const promoteUserInList = (prev, payloadUserId, messageText, messageTime) => {
    if (!payloadUserId) return prev;

    const updated = prev.map((u) =>
      String(u.id) === String(payloadUserId)
        ? {
            ...u,
            lastMessage: messageText || u.lastMessage,
            lastMessageTime: messageTime || u.lastMessageTime,
          }
        : u
    );

    const idx = updated.findIndex((u) => String(u.id) === String(payloadUserId));
    if (idx > 0) {
      const [moved] = updated.splice(idx, 1);
      return [moved, ...updated];
    }
    return updated;
  };

  const appendLiveMessages = (payload, activeUserId = selectedUserRef.current?.id) => {
    const payloadUserId = extractConversationUserId(payload);
    const activeId = String(activeUserId || "");
    const belongsToActiveChat = Boolean(payloadUserId && activeId && payloadUserId === activeId);

    const next = normalizeMessages(payload, belongsToActiveChat ? activeId : payloadUserId);
    if (!next.length) return null;

    const incoming = next[0];
    const payloadKey = getLivePayloadKey(Array.isArray(payload) ? payload[0] : payload);
    const contentKey = getComparableContentKey(incoming);

    if (belongsToActiveChat) {
      setMessages((prev) => {
        if (!incoming.isUser) {
          const optimisticIndex = prev.findIndex((m) => m.optimistic && !m.isUser && getComparableContentKey(m) === contentKey);
          if (optimisticIndex !== -1) {
            const updated = [...prev];
            updated[optimisticIndex] = {
              ...updated[optimisticIndex],
              ...incoming,
              duration: Number.isFinite(incoming?.duration) && incoming.duration > 0
                ? incoming.duration
                : updated[optimisticIndex].duration,
              optimistic: false,
            };
            seenMessageKeysRef.current.add(payloadKey || contentKey);
            seenMessageKeysRef.current.add(getMessageKey(updated[optimisticIndex]));
            return updated;
          }
        }

        if (seenMessageKeysRef.current.has(payloadKey) || seenMessageKeysRef.current.has(contentKey)) {
          return prev;
        }

        seenMessageKeysRef.current.add(payloadKey || contentKey);
        seenMessageKeysRef.current.add(contentKey);
        seenMessageKeysRef.current.add(getMessageKey(incoming));
        return [...prev, incoming];
      });
    }

    return incoming;
  };

  const fetchUsers = async () => {
    if (!token) return;
    setLoadingUsers(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/broadcast/users`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (!res.ok || json?.status === "0") throw new Error(json?.message || "Failed to fetch users");
      const list = normalizeUsers(json);
      setUsers(list);
    } catch (e) {
      toast.error(e.message || "Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mm = window.matchMedia("(max-width: 899px)");
    const onChange = (e) => setIsMobile(e.matches);
    if (mm.addEventListener) mm.addEventListener("change", onChange);
    else mm.addListener(onChange);
    setIsMobile(mm.matches);
    return () => {
      if (mm.removeEventListener) mm.removeEventListener("change", onChange);
      else mm.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    if (!selectedUser?.id) {
      setMessages([]);
      seenMessageKeysRef.current = new Set();
      return;
    }

    const load = async () => {
      if (!token) return;
      setLoadingMessages(true);
      try {
        const params = new URLSearchParams({ user_id: String(selectedUser.id), limit: "500", offset: "0" });
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/broadcast/user/messages?${params.toString()}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (!res.ok || json?.status === "0") throw new Error(json?.message || "Failed to load messages");
        const normalized = normalizeMessages(json);
        rememberMessages(normalized);
        setMessages(normalized);
      } catch (e) {
        toast.error(e.message || "Failed to load messages");
        setMessages([]);
        seenMessageKeysRef.current = new Set();
      } finally {
        setLoadingMessages(false);
      }
    };

    load();
  }, [selectedUser?.id, token]);

  useEffect(() => {
    if (!token) return;
    const socketConfig = getSocketConnectionConfig();
    const socket = io(socketConfig.url, {
      path: socketConfig.path,
      query: { token },
      auth: { token },
      transports: ["polling"],
      upgrade: false,
      timeout: 20000,
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("list_users", { limit: 50, offset: 0 });
      if (selectedUserRef.current?.id) {
        socket.emit("get_user_messages", { user_id: selectedUserRef.current.id, limit: 50, offset: 0 });
      }
    });

    socket.on("connected", () => {
      socket.emit("list_users", { limit: 50, offset: 0 });
    });

    socket.on("connect_error", () => {
      // Silent: polling fallback will keep the inbox updated.
    });

    socket.on("reconnect", () => {
      socket.emit("list_users", { limit: 50, offset: 0 });
      if (selectedUserRef.current?.id) {
        socket.emit("get_user_messages", { user_id: selectedUserRef.current.id, limit: 50, offset: 0 });
      }
    });

    socket.on("users_list", (payload) => {
      const list = normalizeUsers(payload);
      setUsers((prev) => {
        const ids = new Set(prev.map((p) => String(p.id)));
        const merged = [...prev];
        list.forEach((u) => {
          if (!ids.has(String(u.id))) merged.push(u);
        });
        return merged;
      });
    });

    socket.on("user_messages", (payload) => {
      const payloadUserId = extractConversationUserId(payload);
      const activeId = String(selectedUserRef.current?.id || "");
      if (payloadUserId && activeId && payloadUserId !== activeId) return;

      const msgs = normalizeMessages(payload, activeId);
      rememberMessages(msgs);
      setMessages(msgs);
    });

    socket.on("admin_broadcast_inbox_new", (payload) => {
      const payloadUserId = extractConversationUserId(payload);
      const activeId = String(selectedUserRef.current?.id || "");
      const incoming = appendLiveMessages(payload, activeId);
      const previewText = getMessagePreviewText(payload, incoming?.text);
      const previewTime = unwrapSocketPayload(payload)?.created_at || payload?.created_at || payload?.last_message_time;

      if (payloadUserId) {
        setUsers((prev) => promoteUserInList(prev, payloadUserId, previewText, previewTime));
      }
    });

    socket.on("new_message", (payload) => {
      const payloadUserId = extractConversationUserId(payload);
      const activeId = String(selectedUserRef.current?.id || "");
      const incoming = appendLiveMessages([payload], activeId);
      const previewText = getMessagePreviewText(payload, incoming?.text);
      const previewTime = unwrapSocketPayload(payload)?.created_at || payload?.created_at;

      if (payloadUserId) {
        setUsers((prev) => promoteUserInList(prev, payloadUserId, previewText, previewTime));
      }
    });

    socket.on("chat_list_updated", (payload) => {
      const payloadUserId = extractConversationUserId(payload);
      if (!payloadUserId) return;

      const previewText = getMessagePreviewText(payload);
      const previewTime = unwrapSocketPayload(payload)?.created_at || payload?.created_at || payload?.last_message_time;

      setUsers((prev) => promoteUserInList(prev, payloadUserId, previewText, previewTime));
    });

    socket.on("sent_to_user", (payload) => {
      const payloadUserId = extractConversationUserId(payload);
      const activeId = String(selectedUserRef.current?.id || "");
      if (payloadUserId && activeId && payloadUserId === activeId) {
        appendLiveMessages([payload], activeId);
      }

      if (payloadUserId) {
        const previewText = getMessagePreviewText(payload);
        setUsers((prev) => promoteUserInList(prev, payloadUserId, previewText, payload?.created_at));
      }
    });

    return () => {
      socket.off("connect");
      socket.off("users_list");
      socket.off("user_messages");
      socket.off("new_message");
      socket.off("connected");
      socket.off("chat_list_updated");
      socket.off("sent_to_user");
      socket.off("admin_broadcast_inbox_new");
      socket.off("connect_error");
      socket.off("reconnect");
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!messagesRef.current) return;
    try {
      messagesRef.current.scrollTo({ top: messagesRef.current.scrollHeight, behavior: "smooth" });
    } catch (e) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const openUser = (u) => {
    seenMessageKeysRef.current = new Set();
    setMessages([]);
    setSelectedUser(u);
    if (isMobile) {
      setMobileView("detail");
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      });
    }
    const socket = socketRef.current;
    if (socket?.connected && u?.id) {
      socket.emit("get_user_messages", { user_id: u.id, limit: 50, offset: 0 });
    }
  };

  const sendReply = async (e) => {
    e?.preventDefault?.();
    if (!token) return toast.error("Admin session expired");
    const trimmed = text.trim();
    if (!trimmed) return toast.error("Message required");
    if (!selectedUser?.id) return toast.error("Select a user first");

    const socket = socketRef.current;
    if (!socket) {
      toast.error("Socket is not initialized yet. Please wait and try again.");
      return;
    }

    const optimisticId = `tmp_${Date.now()}`;
    setSubmitting(true);
    setMessages((prev) => [...prev, { id: optimisticId, text: trimmed, time: new Date().toISOString(), isUser: false, senderName: "Admin", optimistic: true }]);

    try {
      const socketReady = await new Promise((resolve, reject) => {
        if (socket.connected) {
          resolve(socket);
          return;
        }

        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error("Socket connect timeout"));
        }, 10000);

        const handleConnect = () => {
          cleanup();
          resolve(socket);
        };

        const handleConnectError = (err) => {
          cleanup();
          reject(new Error(err?.message || "Socket connect error"));
        };

        const cleanup = () => {
          clearTimeout(timeout);
          socket.off("connect", handleConnect);
          socket.off("connect_error", handleConnectError);
        };

        socket.on("connect", handleConnect);
        socket.on("connect_error", handleConnectError);

        try {
          socket.connect();
        } catch (err) {
          cleanup();
          reject(new Error(err?.message || "Unable to start socket connection"));
        }
      });

      socketReady.emit("send_to_user", { user_id: String(selectedUser.id), message: trimmed });
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      toast.error(err?.message || "Socket not connected. Please reconnect and try again.");
      setSubmitting(false);
      return;
    }

    setText("");
    setMessages((prev) => prev.map((m) => (m.id === optimisticId ? { ...m, optimistic: false } : m)));
    setSubmitting(false);
  };

  const sendVoiceReply = async ({ blob, filename, mimeType, durationSeconds }) => {
    if (!token) throw new Error("Admin session expired");
    if (!selectedUser?.id) throw new Error("Select a user first");

    const socket = socketRef.current;
    if (!socket) {
      throw new Error("Socket is not initialized yet. Please wait and try again.");
    }

    // Try to convert recorded blob to MP3 client-side. Fall back to original blob on failure.
    let sendBlob = blob;
    let sendFilename = filename || "admin-voice-message.webm";
    let sendMime = mimeType || blob.type || "audio/webm";
    try {
      const mp3 = await convertAudioBlobToMp3(blob);
      if (mp3 && mp3.size > 0) {
        sendBlob = mp3;
        sendFilename = (filename || "admin-voice-message") .replace(/\.(webm|wav|m4a|ogg|mp3)?$/i, "") + ".mp3";
        sendMime = "audio/mpeg";
      }
    } catch (err) {
      }

    const arrayBuffer = await sendBlob.arrayBuffer();
    const localUrl = URL.createObjectURL(sendBlob);
    const optimisticId = `tmp_voice_${Date.now()}`;

    setMessages((prev) => [...prev, {
      id: optimisticId,
      text: "",
      time: new Date().toISOString(),
      isUser: false,
      senderName: "Admin",
      type: "audio",
      fileName: sendFilename,
      fileUrl: localUrl,
      mime: sendMime,
      duration: Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : 0,
      optimistic: true,
    }]);

    if (!socket.connected) {
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          cleanup();
          reject(new Error("Socket connect timeout"));
        }, 10000);

        const handleConnect = () => {
          cleanup();
          resolve();
        };

        const handleConnectError = (err) => {
          cleanup();
          reject(new Error(err?.message || "Socket connect error"));
        };

        const cleanup = () => {
          clearTimeout(timeout);
          socket.off("connect", handleConnect);
          socket.off("connect_error", handleConnectError);
        };

        socket.on("connect", handleConnect);
        socket.on("connect_error", handleConnectError);

        try {
          socket.connect();
        } catch (error) {
          cleanup();
          reject(new Error(error?.message || "Unable to start socket connection"));
        }
      });
    }

    // Send binary ArrayBuffer directly to avoid memory duplication and to allow Socket.IO to use binary transport.
    socket.emit("send_file_to_user", {
      user_id: String(selectedUser.id),
      filename: sendFilename,
      mime_type: sendMime,
      file: arrayBuffer,
      duration: Number.isFinite(durationSeconds) && durationSeconds > 0 ? durationSeconds : undefined,
      message: "",
      caption: "",
      sender_name: "PhoneBookPlus Support",
    });

    window.setTimeout(() => {
      const activeSocket = socketRef.current;
      const activeUserId = selectedUserRef.current?.id;
      if (activeSocket?.connected && activeUserId) {
        activeSocket.emit("get_user_messages", { user_id: String(activeUserId), limit: 50, offset: 0 });
      }
    }, 600);
  };

  return (
    <div className="min-h-screen bg-[#F5F4FF] p-4 md:p-6">
      <div className="max-w-8xl mx-auto grid grid-cols-1 min-[900px]:grid-cols-[360px_minmax(0,1fr)] gap-6 min-h-[calc(100dvh-2rem)] md:min-h-[calc(100dvh-3rem)]">
        <section className={`${isMobile && mobileView === "detail" ? "hidden" : ""} bg-white rounded-2xl p-4 shadow-lg h-[calc(100dvh-2rem)] md:h-[calc(100dvh-3rem)] min-[900px]:sticky min-[900px]:top-6 flex flex-col min-[900px]:w-[360px] min-[900px]:flex-shrink-0`}>
          <div className="flex items-center justify-between gap-3 mb-3">
            <h2 className="text-lg font-semibold text-gray-800">Inbox</h2>
            <button type="button" onClick={fetchUsers} className="text-xs md:text-sm px-3 py-1 rounded-xl border border-[#6D5EF7] text-[#6D5EF7] hover:bg-[#6D5EF7] hover:text-white transition">Refresh</button>
          </div>

          <div className="rounded-[100px] bg-[#797C7B] bg-opacity-[11%] px-4 py-2 mb-3">
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search users" className="w-full bg-transparent text-sm outline-none placeholder:text-gray-500" />
          </div>

          <div className="space-y-3 flex-1 overflow-y-auto pr-1 touch-pan-y overscroll-contain [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100">
            {loadingUsers && <SkeletonList count={6} />}
            {!loadingUsers && filtered.length === 0 && <p className="text-sm text-gray-500">No users found.</p>}
            {!loadingUsers && filtered.map((u) => {
              const isActive = String(selectedUser?.id) === String(u.id);
              return (
                <button key={u.id} onClick={() => openUser(u)} className={`w-full text-left flex items-center gap-3 p-3 py-2 rounded-[100px] border transition ${isActive ? "bg-[#4F46E5] bg-opacity-20 border-[#4F46E5]" : "bg-[#797C7B] bg-opacity-[11%] hover:bg-opacity-20 border-transparent"}`}>
                  <img src={u.avatar} alt={u.name} className="h-10 w-10 rounded-full object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="heading-font truncate">{u.name}</p>
                    <p className="body-font text-xs text-gray-600 truncate">{u.lastMessage || u.phone}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section className={`${isMobile && mobileView === "list" ? "hidden" : ""} bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-white flex flex-col h-[calc(100dvh-2rem)] md:h-[calc(100dvh-3rem)] min-[900px]:h-[calc(100vh-3rem)] min-h-0`}>
          {selectedUser ? (
            <>
              <div className="flex items-center justify-between gap-3  pb-3 mb-3 sticky top-0 bg-white z-10 shrink-0">
                {isMobile && (
                  <button
                    type="button"
                    onClick={() => setMobileView("list")}
                    className="inline-flex items-center text-xl"
                  >
                    ←
                  </button>
                )}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <img src={selectedUser.avatar} alt={selectedUser.name} className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover shrink-0" />
                  <div className="min-w-0 flex-1">
                    <h1 className="text-base sm:text-2xl font-bold text-gray-800 truncate">{selectedUser.name}</h1>
                    <p className="text-sm text-gray-500 truncate">{selectedUser.phone || selectedUser.id}</p>
                  </div>
                </div>
              </div>

              <div ref={messagesRef} className="flex flex-col flex-1 min-h-0 overflow-y-auto space-y-3 pr-1 mb-4 touch-pan-y overscroll-contain [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-gray-100">
                {loadingMessages && <SkeletonList count={8} compact />}
                {!loadingMessages && messages.length === 0 && <p className="text-sm text-gray-500">No messages found for this user.</p>}

                {!loadingMessages && messages.map((m) => {
                  const displayTime = formatMessageTimeUtc(m.time);
                  return (
                    <div key={m.id}>
                      {m.type === "audio" ? (
                        <AudioMessage message={m} isUser={m.isUser} senderName={m.senderName} />
                      ) : (
                        <div className={`flex ${m.isUser ? "justify-start" : "justify-end"}`}>
                          <div className={`max-w-[75%] rounded-2xl px-4 py-3 text-sm shadow-sm ${m.isUser ? "bg-gray-100 text-gray-800" : "bg-[#4F46E5] text-white"}`}>
                            <p className="whitespace-pre-wrap break-words">{m.text}</p>
                            <p className={`mt-1 text-[11px] ${m.isUser ? "text-gray-500" : "text-white/80"}`}>
                              {m.senderName || (m.isUser ? "User" : "Admin")}
                              {displayTime ? ` • ${displayTime}` : ""}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <form onSubmit={sendReply} className="border-t border-gray-100 pt-4 sticky bottom-0 bg-white z-10 items-center shrink-0">
                {/* <div className="w-full">
                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="admin-reply">Reply to {selectedUser.name}</label>
  
              </div> */}
                <div className="flex items-center gap-3">
                  <div className="w-full min-w-0">
                    <input id="admin-reply" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message..."
                      className="pr-20 w-full min-w-0 rounded-[100px] border border-gray-400 bg-white px-4 py-3 text-gray-800 placeholder:text-gray-400 outline-none transition focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15 shadow-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => setVoiceModalOpen(true)}
                    disabled={!selectedUser?.id}
                    className="ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Record voice message"
                    title={selectedUser?.id ? "Record voice message" : "Select a user first"}
                  >
                    <img src="../chatscreen/mic.svg" alt="mic" className="w-8 h-8" />
                  </button>
                  <div>
                    <button type="submit" disabled={submitting} className="disabled:opacity-60 disabled:cursor-not-allowed">
                      {/* {submitting ? "Sending..." : "Send Message"} */}
                      <img
                        src="../chatscreen/send-arrow.svg"
                        alt="send"
                        className="w-8 h-8"
                      />
                    </button>
                  </div>
                </div>
              </form>
              <AdminVoiceSendModal
                open={voiceModalOpen}
                onClose={() => setVoiceModalOpen(false)}
                recipientName={selectedUser?.name}
                onSend={sendVoiceReply}
              />
            </>
          ) : (
            <div className="flex flex-1 flex-col items-center justify-center text-center text-gray-500 px-6">
              <div className="mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-[#F3F0FF]">
                <img src="../chatscreen/message-chat.svg" alt="" className="h-12 w-12 opacity-80" aria-hidden="true" />
              </div>
              <p className="text-lg font-semibold text-gray-700">Select a conversation</p>
              <p className="mt-2 max-w-sm text-sm text-gray-500">
                Choose a user from the inbox to view messages and reply.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
